import axios from 'axios';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export type ReportFormat = 'csv' | 'json';

export interface ReportOptions {
  dateRange: DateRange;
  format: ReportFormat;
  columns?: string[];
}

/**
 * Create a reconciliation report job and return job ID
 */
export const createReconciliationReportJob = async (
  dateRange: DateRange,
  format: ReportFormat = 'csv',
  columns?: string[]
): Promise<{ jobId: number; status: string; createdAt: string }> => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/report-jobs`,
      {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        format,
        columns: columns || []
      }
    );

    return response.data;
  } catch (error) {
    console.error('Failed to create reconciliation report job:', error);
    throw error;
  }
};

/**
 * Get reconciliation report job status
 */
export const getReconciliationReportJobStatus = async (jobId: number): Promise<{
  jobId: number;
  status: string;
  fileBytes?: number;
  sha256?: string;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
}> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/report-jobs/${jobId}`
    );

    return response.data;
  } catch (error) {
    console.error('Failed to get reconciliation report job status:', error);
    throw error;
  }
};

/**
 * Download completed reconciliation report file
 */
export const downloadReconciliationReportFile = async (
  jobId: number,
  filename: string
): Promise<void> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/report-jobs/${jobId}/download`,
      {
        responseType: 'blob'
      }
    );

    // Create download link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download reconciliation report file:', error);
    throw error;
  }
};

/**
 * Download reconciliation report from the backend export endpoint (DEPRECATED)
 * This is kept for backward compatibility but should not be used for large datasets
 */
export const downloadReconciliationReport = async (
  dateRange: DateRange,
  format: ReportFormat = 'csv',
  columns?: string[]
): Promise<void> => {
  try {
    const params: any = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      format
    };

    // Add columns parameter if provided
    if (columns && columns.length > 0) {
      params.columns = columns.join(',');
    }

    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/report/export`,
      {
        params,
        responseType: 'blob'
      }
    );

    // Create download link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `qbo-reconciliation-${dateRange.startDate}-${dateRange.endDate}.${format}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download reconciliation report:', error);
    throw error;
  }
};

export interface QboCompanyInfo {
  companyName: string;
  companyId: string;
  qboCompanyId?: string;
  timezone: string;
  country: string;
  fiscalYearStartMonth: string;
  legalName: string;
  email: string;
  webSite: string;
  lastPing: string;
  realmId: string;
  tokenExpiresAt: string;
}

export interface QboTokenInfo {
  hasToken: boolean;
  isExpired: boolean;
  expiresAt: string | null;
  lastRefreshed: string | null;
  accessTokenExpiry: string | null;
  refreshTokenExpiry: string | null;
  realmId: string | null;
}

export interface QboConnectionStatusResult {
  success: boolean;
  data?: {
    companyName: string;
    companyId: number;
    qboCompanyId: string;
    timezone: string;
    country: string;
    fiscalYearStartMonth: string;
    legalName: string;
    email: string;
    webSite: string;
    lastPing: string;
    realmId: string;
    tokenExpiresAt: string;
  };
  error?: string;
  message?: string;
  tokenStatus?: 'connected' | 'reauthentication_needed' | 'reconnection_required' | 'disconnected' | 'error';
  canRefresh?: boolean;
  canReconnect?: boolean;
  timestamp?: string;
}

/**
 * Ping QBO API to test connection and get company info
 */
// Full ping response type
export type QboPingResponse = QboConnectionStatusResult;

// Legacy type for backward compatibility
export type QboConnectionStatus = {
  companyName: string;
  companyId: number;
  timezone: string;
};

export const pingQboCompany = async (): Promise<QboPingResponse> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/ping`
    );
    return response.data;
  } catch (error) {
    console.error('QBO ping failed:', error);
    throw error;
  }
};

// Legacy function for backward compatibility
export const pingQboCompanyLegacy = async (): Promise<QboConnectionStatus> => {
  try {
    const response = await pingQboCompany();
    if (response.success && response.data) {
      return {
        companyName: response.data.companyName,
        companyId: response.data.companyId,
        timezone: response.data.timezone
      };
    } else {
      throw new Error(response.message || 'QBO ping failed');
    }
  } catch (error) {
    console.error('QBO ping failed:', error);
    throw error;
  }
};

/**
 * Sync invoice to QuickBooks Online by calling the function directly
 */
export const syncInvoiceToQbo = async (invoiceId: number): Promise<any> => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/sync-invoice`,
      { invoiceId }
    );
    return response.data;
  } catch (error) {
    console.error('Sync invoice failed:', error);
    throw error;
  }
};

/**
 * Disconnect from QBO by revoking tokens
 */
export const disconnectQbo = async (): Promise<void> => {
  try {
    await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/disconnect`
    );
  } catch (error) {
    console.error('QBO disconnect failed:', error);
    throw error;
  }
};

/**
 * Start OAuth flow to re-authenticate with QBO
 */
export const reAuthenticateQbo = (): void => {
  window.location.href = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/oauth/start`;
};

/**
 * Force re-authorization by clearing existing tokens and starting fresh OAuth flow
 */
export const forceReconnectQbo = (): void => {
  window.location.href = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/oauth/force-reconnect`;
};

/**
 * Get detailed QBO token information
 */
export const getQboTokenInfo = async (): Promise<QboTokenInfo> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/token-status`
    );
    return response.data.data;
  } catch (error) {
    console.error('Failed to get QBO token info:', error);
    throw error;
  }
};

// Applied Payments Reconciliation (incremental scan)
export type AppliedPaymentsSummary = {
  invoicesToScan: number;
  mismatches: number;
  lastRun: string | null;
};

export type AppliedPaymentScanItem = {
  payment_id: number;
  payment_qbo_id: string | null;
  payment_amount: number;
  received_on: string | null;
  created_at: string | null;
  qbol_sync: boolean | null;
  qbol_message: string | null;
  stripe_id: string | null;
  last4: string | null;
  network: string | null;
  applied_amount: number;
  invoice_id: number;
  invoice_qbo_id: string | null;
  invoice_creation_date: string | null;
} | null;

export const getAppliedPaymentsSummary = async (): Promise<AppliedPaymentsSummary> => {
  const resp = await axios.get(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/applied-payments/summary`
  );
  return resp.data.data as AppliedPaymentsSummary;
};

export const scanOneAppliedPayment = async (): Promise<AppliedPaymentScanItem> => {
  const resp = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/applied-payments/scan`
  );
  return resp.data.data as AppliedPaymentScanItem;
};

// Paginated candidates API
export type AppliedPaymentApplication = {
  invoice_id: number;
  invoice_qbo_id: string | null;
  applied_amount: number;
  can_correct: boolean;
};

export type AppliedPaymentCandidate = {
  payment_id: number;
  payment_qbo_id: string | null;
  payment_amount: number;
  received_on: string | null;
  created_at: string | null;
  qbol_sync: boolean | null;
  qbol_message: string | null;
  stripe_id: string | null;
  last4: string | null;
  network: string | null;
  fk_bcustomer_id: number | null;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  customer_qbo_id: string | null;
  customer_qbol_sync: boolean | null;
  customer_qbol_message: string | null;
  applied_total: number;
  applications: AppliedPaymentApplication[];
};

export type AppliedPaymentCandidatesPage = {
  items: AppliedPaymentCandidate[];
  total: number;
  page: number;
  pageSize: number;
};

export const getAppliedPaymentCandidates = async (page = 1, pageSize = 25, search = ''): Promise<AppliedPaymentCandidatesPage> => {
  const resp = await axios.get(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/applied-payments/candidates`,
    { params: { page, pageSize, search } }
  );
  return resp.data.data as AppliedPaymentCandidatesPage;
};

// QBO details fetch for selected payments/invoices
export type AppliedPaymentsQboDetails = {
  payments: Array<{ id: string; success: boolean; data?: any; error?: any; isActive?: boolean }>;
  invoices: Array<{ id: string; success: boolean; data?: any; error?: any; isActive?: boolean }>;
  customers: Array<{ id: string; success: boolean; data?: any; error?: any; isActive?: boolean }>;
  realmId: string;
};

/**
 * Sync a single payment using the new reconciliation sync module
 */
export const syncPaymentForReconciliation = async (paymentId: number): Promise<{
  success: boolean;
  message: string;
  qboId?: string;
  error?: string;
}> => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/sync/payment`,
    { paymentId }
  );
  return response.data;
};

/**
 * Sync a single invoice using the new reconciliation sync module
 */
export const syncInvoiceForReconciliation = async (invoiceId: number): Promise<{
  success: boolean;
  message: string;
  qboId?: string;
  error?: string;
}> => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/sync/invoice`,
    { invoiceId }
  );
  return response.data;
};

/**
 * Sync multiple payments using the new reconciliation sync module
 */
export const syncPaymentsForReconciliation = async (paymentIds: number[]): Promise<{
  success: boolean;
  message: string;
  data: {
    total: number;
    successful: number;
    failed: number;
    results: Array<{
      success: boolean;
      qboId?: string;
      error?: string;
      paymentId?: number;
    }>;
  };
}> => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/sync/payments`,
    { paymentIds }
  );
  return response.data;
};

/**
 * Sync multiple invoices using the new reconciliation sync module
 */
export const syncInvoicesForReconciliation = async (invoiceIds: number[]): Promise<{
  success: boolean;
  message: string;
  data: {
    total: number;
    successful: number;
    failed: number;
    results: Array<{
      success: boolean;
      qboId?: string;
      error?: string;
      invoiceId?: number;
    }>;
  };
}> => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/sync/invoices`,
    { invoiceIds }
  );
  return response.data;
};

/**
 * Sync a single customer using the new reconciliation sync module
 */
export const syncCustomerForReconciliation = async (customerId: number): Promise<{
  success: boolean;
  message: string;
  qboId?: string;
  error?: string;
  data?: any;
}> => {
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/sync/customer`,
    { customerId }
  );

  const payload = response.data || {};
  const derivedQboId = payload.qboId
    ?? payload.data?.qboId
    ?? payload.data?.customer?.qboId;

  return {
    ...payload,
    qboId: derivedQboId,
  };
};

export const fetchAppliedPaymentsQboDetails = async (paymentQboIds: string[], invoiceQboIds: string[] = [], customerQboIds: string[] = []): Promise<AppliedPaymentsQboDetails> => {
  const resp = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/applied-payments/qbo-details`,
    { paymentQboIds, invoiceQboIds, customerQboIds }
  );
  return resp.data.data as AppliedPaymentsQboDetails;
};

// Apply corrections in QBO using sparse update
export type CorrectionApplication = {
  invoiceId?: number | null;
  invoiceQboId?: string | null;
  amountCents: number; // stored in cents in EServ, convert when syncing to QBO
};

export type CorrectionItem = {
  paymentQboId: string;
  paymentId?: number; // local EServ payment id for DB update
  applications: CorrectionApplication[];
};
export type CorrectionResult = { 
  paymentQboId: string; 
  ok: boolean; 
  data?: any; 
  error?: any; 
  details?: {
    missingInvoices?: string[];
    capped?: boolean;
    unapplied?: number;
    total?: number;
    appliedLines?: any[];
    updateSuccessful?: boolean;
    actualUnapplied?: number;
    expectedUnapplied?: number;
    actualLineCount?: number;
    expectedLineCount?: number;
    syncResults?: Array<{
      localId: number;
      qboId: string;
      success: boolean;
      result?: any;
      error?: string;
    }>;
    failedSyncs?: number[];
    taxErrors?: number[];
  };
};

export const correctAppliedPayments = async (
  items: CorrectionItem[]
): Promise<{ results: CorrectionResult[]; realmId: string }> => {
  const resp = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/applied-payments/correct`,
    { items }
  );
  return resp.data.data as { results: CorrectionResult[]; realmId: string };
};

// Apply individual payment to invoice in QBO
export type ApplyPaymentRequest = {
  invoiceId: number;
  paymentId: number;
  qbolInvoiceId: string;
  qbolPaymentId: string;
  amount: number; // in cents
};

export type ApplyPaymentResult = {
  invoiceId: number;
  qbolId: string;
  paymentId: number;
  qbolPaymentId: string;
  amount: number;
  action: 'Linked Payment' | 'Skipped' | 'Failed';
  reason: string;
};

export const applyPaymentToInvoice = async (
  request: ApplyPaymentRequest
): Promise<ApplyPaymentResult> => {
  const resp = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/applied-payments/apply-payment`,
    request
  );
  return resp.data.data as ApplyPaymentResult;
};

// File Management Types
export interface ReportFile {
  fileName: string;
  jobId: number;
  format: 'csv' | 'json';
  size: number;
  createdAt: string;
  modifiedAt: string;
  filePath: string;
}

export interface ReportFilesResponse {
  success: boolean;
  files: ReportFile[];
  total: number;
}

/**
 * Get list of all report jobs for the company
 */
export const getReportJobs = async (): Promise<{
  success: boolean;
  jobs: Array<{
    jobId: number;
    status: string;
    fileBytes?: number;
    createdAt: string;
    startedAt?: string;
    finishedAt?: string;
    params: {
      startDate: string;
      endDate: string;
      format: string;
      columns: string[];
    };
  }>;
  total: number;
}> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/report-jobs`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get report jobs:', error);
    throw error;
  }
};

/**
 * Get list of all report files for the company
 */
export const getReportFiles = async (): Promise<ReportFilesResponse> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/report-jobs/files`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get report files:', error);
    throw error;
  }
};

/**
 * Delete a specific report file
 */
export const deleteReportFile = async (jobId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/report-jobs/files/${jobId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to delete report file:', error);
    throw error;
  }
};

/**
 * Delete all report files for the company
 */
export const deleteAllReportFiles = async (): Promise<{ 
  success: boolean; 
  message: string; 
  deletedCount: number; 
  errors?: Array<{ fileName: string; error: string }> 
}> => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/report-jobs/files`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to delete all report files:', error);
    throw error;
  }
};
